import { useContext, type ComponentProps, type PropsWithChildren } from "react";
import { UNSAFE_PortalProvider as PortalProvider } from "react-aria";
import {
  MenuTrigger,
  Pressable,
  type PopoverProps,
  type MenuItemProps as MenuItemPrimitiveProps,
} from "react-aria-components";
import { CoreMenu, type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./MenuV2.styled";
import { type Placement } from "./MenuV2.types";
import { MAX_LIST_HEIGHT } from "./MenuV2.constants";
import MenuV2Context, { MenuV2Provider } from "./MenuV2Context";
import { useOverlay } from "../OverlayContext";

export type MenuV2Props = PropsWithOptionalTestID &
  PropsWithChildren<{
    popoverProps: Pick<
      PopoverProps,
      "offset" | "containerPadding" | "maxHeight" | "style"
    > & {
      placement?: Placement;
    };
  }>;

function MenuV2(props: MenuV2Props) {
  const { children, testID = "menu", ...otherProps } = props;

  return (
    <MenuV2Provider {...otherProps} testID={testID}>
      <MenuTrigger>{children}</MenuTrigger>
    </MenuV2Provider>
  );
}

type PressableProps = ComponentProps<typeof Pressable>;
type MenuV2TriggerProps = Pick<PressableProps, "children">;

MenuV2.Trigger = function MenuV2Trigger(props: MenuV2TriggerProps) {
  return <Pressable {...props} />;
};

type MenuV2ContentProps = PropsWithChildren;

MenuV2.Content = function MenuV2Content(props: MenuV2ContentProps) {
  const { children } = props;

  const { popoverProps, testID } = useContext(MenuV2Context);

  const { portalElement } = useOverlay();

  return (
    <PortalProvider getContainer={portalElement && (() => portalElement)}>
      <S.Popover
        offset={CoreMenu.DEFAULT_OFFSET}
        maxHeight={MAX_LIST_HEIGHT}
        {...popoverProps}
      >
        <S.Menu data-testid={testID}>{children}</S.Menu>
      </S.Popover>
    </PortalProvider>
  );
};

export type MenuV2ItemProps = MenuItemPrimitiveProps;

MenuV2.Item = function MenuV2Item(props: MenuV2ItemProps) {
  const { children, ...otherProps } = props;

  return (
    <S.MenuItem {...otherProps}>
      {(renderProps) => (
        <S.Content>
          {typeof children === "function" ? children(renderProps) : children}
        </S.Content>
      )}
    </S.MenuItem>
  );
};

export default MenuV2;
